/**
 * The cradle bay frame on a ship (M10 P5a9a, plan 2026-10-17-m10-p5a, Q9). An L hull whose asset carries a
 * `DOCK_CRADLE` socket of `size: "S"` (the Gannet's dorsal cradle, D-700) is a dockable bay that moves with the
 * ship: `cradleFrame` rebuilds its `BayFrame` from the ship's pose through `bayFrameFromSocket` (`bay.ts`), exactly
 * as a station bay is built from the station's pose, so the dock autopilot can fly against it unchanged.
 *
 * Tuning lives in `src/data/cradle.json`, Zod-validated here. Pure maths: no runtime wiring yet (P5a9b wires the
 * flow). `cradleSocketOf` runs once per hull; `cradleFrame`, `cradleAccepts` and `cradleHolding` run every tick
 * and allocate nothing (D-014; `cradle.test.ts` scans them).
 */
import { z } from 'zod'
import type { Quat, Vec3 } from '../core/math'
import type { AssetsFile } from '../data/assetSchema'
import { bayAccepts } from '../data/bands'
import cradleJson from '../data/cradle.json'
import type { ShipBand, ShipDef } from '../data/schemas'
import type { BayFrame } from '../space/contracts'
import { bayFrameFromSocket, type BaySocket } from './bay'
import type { MoorConfig } from './moorConfig'

/** The socket name of a ship-borne S bay (assets.json, `p4bLargeBlocks.test.ts`). */
export const CRADLE_SOCKET = 'DOCK_CRADLE'
/** The only cradle size this phase docks (an S into an L's cradle). */
export const CRADLE_SIZE = 'S'

const DEG = Math.PI / 180

export const cradleConfigSchema = z.strictObject({
  comment: z.string().optional(),
  /** The range from the L (m) at which the dock key may request its cradle (= `local.json dockStartM`). */
  requestRangeM: z.number().positive(),
  /** The cradle accepts only while the L turns at or under this rate (°/s); the speed bound is `moor.holdSpeedMps`. */
  holdTurnDegS: z.number().positive(),
  /** Release speed (m/s) out through the cradle mouth on launch. */
  launchSpeedMps: z.number().positive(),
  /** Unload time once cradled (s); 0 is instant. */
  unloadS: z.number().nonnegative(),
})

export type CradleConfig = z.infer<typeof cradleConfigSchema>

/** Parses and validates cradle.json (throws a ZodError naming the bad field). */
export function loadCradleConfig(raw: unknown = cradleJson): CradleConfig {
  return cradleConfigSchema.parse(raw)
}

let cached: CradleConfig | undefined

/** The parsed cradle.json (cached). */
export function cradleConfig(): CradleConfig {
  cached ??= loadCradleConfig()
  return cached
}

/** What `cradleSocketOf` reads: the resolved defs (id → asset) and the asset manifest. */
export interface CradleData {
  ships: readonly Pick<ShipDef, 'id' | 'assetId'>[]
  assets: Pick<AssetsFile, 'models'>
}

/**
 * The `DOCK_CRADLE` socket of def `defId`'s asset when it is an S cradle, else null (no def, no asset, no socket,
 * or a socket of another size). Runs once per hull, not per tick.
 */
export function cradleSocketOf(defId: string, data: CradleData): BaySocket | null {
  const def = data.ships.find((d) => d.id === defId)
  if (def === undefined) return null
  const entry = data.assets.models.find((m) => m.id === def.assetId)
  if (entry === undefined) return null
  const socket = entry.sockets.find((s) => s.name === CRADLE_SOCKET)
  if (socket === undefined || socket.params.size !== CRADLE_SIZE) return null
  return socket
}

/**
 * The cradle's bay frame for a ship at `shipPos` (rebased to the camera or physics origin, D-013) turned by
 * `shipRot`, written into the pooled `out` and returned. Rebuild it every tick: the bay moves with the ship.
 * Allocation-free (D-014).
 */
export function cradleFrame(shipPos: Readonly<Vec3>, shipRot: Readonly<Quat>, socket: BaySocket, out: BayFrame): BayFrame {
  return bayFrameFromSocket(shipPos, shipRot, socket, out)
}

/** Whether an S cradle takes a hull of `def`'s band: the S bay's `bands.json bayAccepts` row (S only). Allocation-free. */
export function cradleAccepts(def: Readonly<{ sizeBand: ShipBand }>): boolean {
  return bayAccepts(CRADLE_SIZE, def.sizeBand)
}

/** The L's motion the holding check reads: world velocity (m/s) and angular velocity (rad/s). */
export interface CradleHolder {
  vel: Readonly<Vec3>
  angVel: Readonly<Vec3>
}

/**
 * Whether the L holds still enough for its cradle to accept (Q9): speed at or under `moor.holdSpeedMps` and turn
 * rate at or under `cradle.holdTurnDegS`. Allocation-free.
 */
export function cradleHolding(l: Readonly<CradleHolder>, moor: Readonly<Pick<MoorConfig, 'holdSpeedMps'>>, cradle: Readonly<Pick<CradleConfig, 'holdTurnDegS'>>): boolean {
  const v = l.vel
  const w = l.angVel
  const speed = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
  const turn = Math.sqrt(w.x * w.x + w.y * w.y + w.z * w.z)
  return speed <= moor.holdSpeedMps && turn <= cradle.holdTurnDegS * DEG
}
